/*
 * helper functions for payment processing.
 * Types of payments: card and paypal
 * types of actions performed: addSubscription and changeService
 * types of payment states: new and saved. only card is editable as of now.
 * */
import { paypalAddSubscription, paypalChangeSubscription } from 'Api/PayPal';
import { applepaySubscription } from 'Api/ApplePay';
import { segmentTrackEvent, trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { getToday } from 'Utils/Date';
import { cardPaymentAddSubscription, cardPaymentChangeSubscription } from 'Api/EvergentApi';
import {
  FREE_TRIAL_PROMO,
  FIRST_MONTH_99C_PROMO,
  FIRST_MONTH_FREE_PROMO,
  SHEER_YEAR_FREE_PROMO,
  MONTHLY_30_DFT_FREE_PROMO,
  VETERANS_ADV_FREE_PROMO,
} from 'Utils/Constants';
import {
  savedPaymentAddSubscription,
  couponPaymentAddSubscription,
  savedPaymentChangeSubscription,
} from 'Api/AccountApi';

const PROMOTIONS_MAP = [
  FREE_TRIAL_PROMO,
  FIRST_MONTH_99C_PROMO,
  FIRST_MONTH_FREE_PROMO,
  SHEER_YEAR_FREE_PROMO,
  MONTHLY_30_DFT_FREE_PROMO,
  VETERANS_ADV_FREE_PROMO,
];

export const getCardPaymentMethodId = (paymentMethod) => {
  const { paymentMethodId } = paymentMethod;
  return { label: 'Card', cardInfoMsg: { paymentMethodId } };
};

export const getPaypalPaymentMethodId = (paymentMethod) => {
  const { paymentMethodId } = paymentMethod;
  return { label: 'PayPalAccount', paypalAccountMsg: { paymentMethodId } };
};

export const getPaymentMethodInfoByLabel = (paymentMethod) => {
  const { label } = paymentMethod;
  switch (label) {
    case 'Card':
      return getCardPaymentMethodId(paymentMethod.cardInfoMessage);
    case 'PayPal Account':
      return getPaypalPaymentMethodId(paymentMethod.PayPalAccountMessage);
    default:
      return {};
  }
};

export const paypalPaymentSubscription = (payload) => {
  const { purchaseMode } = payload;
  if (purchaseMode.isChangePlan) {
    return paypalChangeSubscription(payload);
  }
  return paypalAddSubscription(payload);
};

export const applepayPaymentSubscription = (payload) => applepaySubscription(payload);

export const cardPaymentSubscription = (payload) => {
  const { purchaseMode } = payload;
  if (purchaseMode && purchaseMode.isChangePlan) {
    return cardPaymentChangeSubscription(payload);
  }
  return cardPaymentAddSubscription(payload);
};

export const savedPaymentSubscription = (payload) => {
  const { purchaseMode } = payload;
  if (purchaseMode.isChangePlan) {
    return savedPaymentChangeSubscription(payload);
  }
  return savedPaymentAddSubscription(payload);
};

export const freePromoPaymentSubscription = (payload) => {
  return couponPaymentAddSubscription(payload);
};

export const selectPaymentMethodByType = (type, payload) => {
  switch (type) {
    case 'card':
      return cardPaymentSubscription(payload);
    case 'paypal':
      return paypalPaymentSubscription(payload);
    case 'apple pay':
      return applepayPaymentSubscription(payload);
    case 'saved':
      return savedPaymentSubscription(payload);
    case 'coupon':
      return freePromoPaymentSubscription(payload);
    default:
      return Promise.reject(new Error('Please select a payment method'));
  }
};

export const applyDefaultPromo = ({ subscription, plans, promoCode }) => {
  if (
    plans.showDefaultPromo &&
    (subscription.isNewUser || subscription.isChurnedUser) &&
    plans.isPlanSelected &&
    plans.selectedPlan &&
    !promoCode.isValid
  ) {
    const { freePromo, hasFreePromo } = plans.selectedPlan;
    // hasFreePromo flag currently is also applied to promos that are not free. So we need to check for an amount too.
    return (
      hasFreePromo && (plans.showFreeTrialPromo || !!freePromo.amount) && PROMOTIONS_MAP.includes(freePromo.promotionID)
    );
  }
  return false;
};

// payment tracking helpers
export const trackPaymentStarted = () => trackEvent(TRACKING_ENUMS.EVENT_PAYMENT_STARTED);

export const trackPaymentCompleted = ({
  trackPaymentMethod,
  orderID,
  user,
  promoCode,
  pageStartSourceData,
  pageFlowTypeData,
}) => {
  const pageStartSource = pageStartSourceData ? { page_start_source: pageStartSourceData } : null;
  const pageFlowType = pageFlowTypeData ? { page_plan_flow_type: pageFlowTypeData } : null;

  const analyticsProps = {
    page_order_id: orderID,
    page_payment_method: trackPaymentMethod,
    ...pageFlowType,
    ...pageStartSource,
    order_id: orderID,
  };

  trackEvent(TRACKING_ENUMS.EVENT_PAYMENT_COMPLETED, analyticsProps, {
    ...analyticsProps,
    page_plan_date: getToday(),
    promo_code: promoCode.couponCode,
    dcg_profile_id: user.profileId,
  });
};

export const trackPaymentError = ({ trackPaymentMethod, errorMessage, user }) => {
  trackEvent(
    TRACKING_ENUMS.EVENT_PAYMENT_ERROR,
    {
      page_payment_method: trackPaymentMethod,
      page_error_type: 'server-side',
      page_error_description: errorMessage,
    },
    {
      error_type: 'server-side',
      error_message: errorMessage,
      dcg_profile_id: user.profileId,
    }
  );
};

export const trackPaymentAction = (actionType, customProperties) => {
  segmentTrackEvent(TRACKING_ENUMS.EVENT_PAYMENT_ACTION, { page_action_type: actionType, ...customProperties });
};

export const getPromoCode = ({ hasApplied, isValid, couponCode }) => (hasApplied && isValid ? couponCode : '');
